import { useRef, useState } from 'react';
import { extractVintedList } from '../services/mlService';
import { normCode, formatCode, rememberPairs, planUpdates } from '../services/vintedNames';

/**
 * 📦 Vinted-Namen per Screenshot (Sonderfunktion „vintedImport").
 * 1) Screenshots der Vinted-Go-Shops-Liste wählen (mehrere erlaubt — die Liste scrollt)
 * 2) Server liest Paare „300-352 → Josje Heezen"
 * 3) Vorschau → „Opslaan": Paare 24 h merken + vorhandene „Vinted 300-352"-Pakete umbenennen
 */
export default function VintedImport({ pakete, onApply, onClose, t, colors }) {
  const inputRef = useRef(null);
  const [phase, setPhase] = useState('start'); // start | reading | preview | saving | done | error
  const [progress, setProgress] = useState('');
  const [pairs, setPairs] = useState([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const dark = colors?.darkMode;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setPhase('reading');
    setError('');
    const merged = new Map(pairs.map((p) => [p.code, p]));
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress(`${i + 1}/${files.length}`);
        const entries = await extractVintedList(files[i]);
        for (const en of entries) {
          const c = normCode(en.code);
          if (c && en.name) merged.set(c, { code: c, name: String(en.name).trim() });
        }
      }
      setPairs(Array.from(merged.values()));
      setPhase('preview');
    } catch (err) {
      setError(err && err.status === 403 ? t('vinted.notEnabled') : t('vinted.error'));
      setPhase(merged.size ? 'preview' : 'error');
      if (merged.size) setPairs(Array.from(merged.values()));
    }
  };

  const updates = planUpdates(pakete, pairs);
  const matchedCodes = new Set(updates.map((u) => u.code));

  const save = async () => {
    setPhase('saving');
    rememberPairs(pairs);
    let applied = 0;
    try {
      if (updates.length && onApply) applied = await onApply(updates);
    } catch { /* Zähler bleibt; Namen sind trotzdem gemerkt */ }
    setResult({ applied: typeof applied === 'number' ? applied : updates.length, remembered: pairs.length });
    setPhase('done');
  };

  const box = {
    background: dark ? '#23272f' : '#fff', color: dark ? '#fff' : '#23272f', borderRadius: 14,
    width: '100%', maxWidth: 460, maxHeight: '85vh', overflow: 'auto', padding: 16,
    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
  };
  const btn = (primary) => ({
    width: '100%', padding: '12px', borderRadius: 10, border: primary ? 'none' : `1px solid ${dark ? '#444' : '#ddd'}`,
    background: primary ? '#1976d2' : 'transparent', color: primary ? '#fff' : (dark ? '#fff' : '#23272f'),
    fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 10,
  });
  const sub = { fontSize: 13, color: dark ? '#aaa' : '#666', lineHeight: 1.5 };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget && phase !== 'reading' && phase !== 'saving') onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={box} data-testid="vinted-import">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 16 }}>📦 {t('vinted.title')}</strong>
          {phase !== 'reading' && phase !== 'saving' && (
            <button type="button" onClick={onClose} aria-label={t('common.cancel')}
              style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'inherit' }}>✕</button>
          )}
        </div>

        <input ref={inputRef} type="file" accept="image/*" multiple onChange={onFiles} style={{ display: 'none' }} />

        {(phase === 'start' || phase === 'error') && (
          <>
            <div style={sub}>{t('vinted.explain')}</div>
            {error && <div style={{ color: '#e5484d', fontSize: 13, marginTop: 8 }}>⚠️ {error}</div>}
            <button type="button" style={btn(true)} onClick={() => inputRef.current?.click()}>🖼️ {t('vinted.pick')}</button>
          </>
        )}

        {phase === 'reading' && (
          <div style={{ ...sub, textAlign: 'center', padding: '24px 0' }}>⏳ {t('vinted.reading')} {progress}</div>
        )}

        {phase === 'preview' && (
          <>
            {error && <div style={{ color: '#e5484d', fontSize: 13, marginBottom: 8 }}>⚠️ {error}</div>}
            {pairs.length === 0 ? (
              <div style={sub}>{t('vinted.none')}</div>
            ) : (
              <>
                <div style={sub}>{t('vinted.found', { count: pairs.length, matched: updates.length })}</div>
                <div style={{ marginTop: 8, borderTop: `1px solid ${dark ? '#444' : '#eee'}` }}>
                  {pairs.map((p) => (
                    <div key={p.code} style={{ display: 'flex', gap: 10, padding: '7px 0', fontSize: 14,
                      borderBottom: `1px solid ${dark ? '#333' : '#f0f0f0'}` }}>
                      <span style={{ fontFamily: 'monospace', minWidth: 64 }}>{formatCode(p.code)}</span>
                      <span style={{ flex: 1, fontWeight: 600 }}>{p.name}</span>
                      <span style={{ fontSize: 12, color: matchedCodes.has(p.code) ? '#2e7d32' : (dark ? '#999' : '#888') }}>
                        {matchedCodes.has(p.code) ? `✓ ${t('vinted.matched')}` : t('vinted.remembered')}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <button type="button" style={btn(false)} onClick={() => inputRef.current?.click()}>➕ {t('vinted.more')}</button>
            {pairs.length > 0 && <button type="button" style={btn(true)} onClick={save}>✓ {t('vinted.save')}</button>}
          </>
        )}

        {phase === 'saving' && <div style={{ ...sub, textAlign: 'center', padding: '24px 0' }}>⏳ …</div>}

        {phase === 'done' && result && (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#2e7d32', marginBottom: 6 }}>✓ {t('vinted.saved', { applied: result.applied })}</div>
            <div style={sub}>{t('vinted.savedHint', { remembered: result.remembered })}</div>
            <button type="button" style={btn(true)} onClick={onClose}>OK</button>
          </>
        )}
      </div>
    </div>
  );
}
